import React from 'react';
import {makeStyles} from 'tss-react/mui';
import {FormattedMessage} from 'react-intl';
import CloseIcon from '@mui/icons-material/Close';
import {Black, Orange} from 'wobi-web-common/dist/Colors';

const useStyles = makeStyles()((theme) => {
  const primaryMain = theme?.palette?.primary?.main || Orange[440];
  return {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1300,
      padding: 16,
    },
    modal: {
      backgroundColor: '#fff',
      borderRadius: 20,
      width: '100%',
      maxWidth: 380,
      maxHeight: '90vh',
      overflowY: 'auto',
      padding: '24px 16px 20px',
      position: 'relative',
    },
    closeButton: {
      position: 'absolute',
      top: 12,
      left: 12,
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: 4,
      color: Black[220],
      lineHeight: 0,
    },
    title: {
      fontSize: 20,
      fontWeight: 700,
      fontFamily: 'Ploni, Arial, sans-serif',
      color: Black[500],
      textAlign: 'center',
      marginBottom: 20,
    },
    cardSection: {
      border: `1px solid ${Black[100]}`,
      borderRadius: 14,
      padding: '14px 10px',
      marginBottom: 16,
      backgroundColor: '#fff',
    },
    cardTitle: {
      fontSize: 14,
      fontWeight: 600,
      fontFamily: 'Ploni, Arial, sans-serif',
      color: Black[500],
      textAlign: 'center',
      marginBottom: 10,
    },
    bioCard: {
      position: 'relative',
      direction: 'ltr',
      borderRadius: 10,
      backgroundColor: '#f5f7fa',
      border: '1px solid #e8ecf0',
      padding: '16px 12px 44px',
      display: 'flex',
      gap: 8,
      minHeight: 150,
    },
    bioRight: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 4,
      flex: 1,
    },
    bioLeft: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 6,
    },
    photoPlaceholder: {
      width: 48,
      height: 58,
      borderRadius: 4,
      backgroundColor: '#dde2e8',
    },
    chipSmall: {
      width: 32,
      height: 24,
      borderRadius: 4,
      marginBottom: 20,
      marginTop: 6,
      background: 'linear-gradient(135deg, #c9a227 0%, #f0d060 50%, #c9a227 100%)',
    },
    fieldGray: {
      height: 6,
      borderRadius: 3,
      backgroundColor: '#d8dde3',
    },
    bioDateRow: {
      position: 'absolute',
      bottom: 6,
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      alignItems: 'center',
      gap: 4,
    },
    dateHighlight: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      border: `2.5px solid ${primaryMain}`,
      borderRadius: 6,
      padding: '2px 8px',
      backgroundColor: '#fff',
    },
    dateLabel: {
      fontSize: 9,
      fontFamily: 'Ploni, Arial, sans-serif',
      color: Black[300],
      whiteSpace: 'nowrap',
    },
    dateValue: {
      fontSize: 12,
      fontWeight: 700,
      fontFamily: 'Ploni, Arial, sans-serif',
      color: Black[500],
    },
    regCard: {
      position: 'relative',
      borderRadius: 10,
      direction: 'ltr',
      backgroundColor: '#faf8f4',
      border: '1px solid #e8e4dc',
      padding: '16px 12px 44px',
      display: 'flex',
      gap: 8,
      minHeight: 150,
    },
    regLeft: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    regRight: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      direction: 'ltr',
    },
    regFieldRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      direction: 'ltr',
      justifyContent: 'flex-start',
    },
    regField: {
      height: 6,
      borderRadius: 3,
      backgroundColor: '#d8dde3',
      marginRight: 4,
    },
    regBottom: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      direction: 'ltr',
      marginTop: 10,
      gap: 4,
    },
    ctaButton: {
      display: 'block',
      width: '100%',
      padding: '14px',
      borderRadius: 30,
      border: 'none',
      backgroundColor: primaryMain,
      color: '#fff',
      fontSize: 17,
      fontWeight: 700,
      fontFamily: 'Ploni, Arial, sans-serif',
      cursor: 'pointer',
      marginTop: 8,
    },
  };
});

const IdCardHelpIllustration = ({onClose}) => {
  const {classes} = useStyles();

  return (
    <div className={classes.overlay} role='button' tabIndex={0} onClick={onClose} onKeyDown={(e) => e.key === 'Escape' && onClose()}>
      <div className={classes.modal} role='dialog' aria-modal='true' onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <button type='button' className={classes.closeButton} onClick={onClose}>
          <CloseIcon fontSize='small' />
        </button>

        <div className={classes.title}>
          <FormattedMessage id='docCollection.harHabituch.helpIllustration.title' />
        </div>

        {/* Biometric ID Card */}
        <div className={classes.cardSection}>
          <div className={classes.cardTitle}>
            <FormattedMessage id='docCollection.harHabituch.helpIllustration.biometric' />
          </div>
          <div className={classes.bioCard}>
            {/* Right side: photo */}
            <div className={classes.bioLeft}>
              <div className={classes.photoPlaceholder} />
            </div>
            {/* Left side: chip + fields */}
            <div className={classes.bioRight}>
              <div className={classes.fieldGray} style={{width: '70%'}} />
              <div className={classes.fieldGray} style={{width: '55%'}} />
              <div className={classes.fieldGray} style={{width: '45%'}} />
              <div className={classes.fieldGray} style={{width: '60%'}} />
            </div>

            <div className={classes.chipSmall} />
            {/* Bottom center: issuance date */}
            <div className={classes.bioDateRow}>
              <div className={classes.dateHighlight}>
                <span className={classes.dateLabel}>
                  <FormattedMessage id='docCollection.harHabituch.helpIllustration.issuanceDate' />
                </span>
                <span className={classes.dateValue}>27.06.2016</span>
              </div>
            </div>
          </div>
        </div>

        {/* Regular ID Card */}
        <div className={classes.cardSection}>
          <div className={classes.cardTitle}>
            <FormattedMessage id='docCollection.harHabituch.helpIllustration.regular' />
          </div>
          <div className={classes.regCard}>
            {/* Right side: fields RTL */}
            <div className={classes.regRight}>
              <div className={classes.regFieldRow}>
                <div className={classes.regField} style={{width: '50%'}} />
              </div>
              <div className={classes.regFieldRow}>
                <div className={classes.regField} style={{width: '40%'}} />
              </div>
              <div className={classes.regFieldRow}>
                <div className={classes.regField} style={{width: '35%'}} />
              </div>
              <div className={classes.regFieldRow}>
                <div className={classes.regField} style={{width: '30%',marginBottom: 10}} />
              </div>
              {/* Date INSIDE card at bottom, RTL */}
              <div className={classes.regBottom}>
                <div className={classes.dateHighlight}>
                  <span className={classes.dateLabel}>
                    <FormattedMessage id='docCollection.harHabituch.helpIllustration.issuedIn' />
                  </span>
                  <span className={classes.dateValue}>ירושלים 22.07.2012 </span>
                </div>
              </div>

            </div>
            {/* Left side: photo */}
            <div className={classes.regLeft}>
              <div className={classes.photoPlaceholder} />
            </div>
          </div>
        </div>

        <button type='button' className={classes.ctaButton} onClick={onClose}>
          <FormattedMessage id='docCollection.harHabituch.helpIllustration.cta' />
        </button>
      </div>
    </div>
  );
};

export default IdCardHelpIllustration;